import { isEmptyObject, isPresent, mapValues, toBoolean } from '@fcom/core/utils';
import {
  Category,
  FinnairBoundItem,
  FinnairItineraryItemFlight,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceCatalogV3,
} from '@fcom/dapi/api/models';
import { NotificationTheme } from '@fcom/ui-components/components/notifications';

import { PassengerCountQuota, PassengerCountQuotaType } from '../interfaces';
import { isFlight } from '../../../utils/flight.utils';

export interface FragmentQuota {
  fragmentId: string;
  quota: number;
}

interface QuotaBearingService {
  isService?: boolean;
  quota?: number | null;
  variant: string;
}

export type QuotaBearingServicesPerTraveler = Record<string, QuotaBearingService[]>;
export type QuotaBearingServiceCatalogServices = Record<string, QuotaBearingServicesPerTraveler>;

export type VariantUnits = Record<string, number>;
export type VariantUnitsPerTraveler = Record<string, VariantUnits>;
export type VariantUnitsPerFragment = Record<string, VariantUnitsPerTraveler>;

export const SUFFICIENT_QUOTA_SENTINEL = 9;

// The API caps quota at 9, where 9 stands for "9 or more", so only values below it are counts we know exactly.
export const isKnownQuota = (quota: number | null | undefined): quota is number =>
  isPresent(quota) && quota < SUFFICIENT_QUOTA_SENTINEL;

export const isLowQuota = (remaining: number | null | undefined): boolean => isKnownQuota(remaining) && remaining > 0;

export const CATEGORY_LEVEL_QUOTA_CATEGORIES: Category[] = [Category.MEAL, Category.PRIORITY];
export const VARIANT_LEVEL_QUOTA_CATEGORIES: Category[] = [Category.SPORT];

export const hasCategoryLevelQuota = (category: Category): boolean =>
  CATEGORY_LEVEL_QUOTA_CATEGORIES.includes(category);

export const hasVariantLevelQuota = (category: Category): boolean => VARIANT_LEVEL_QUOTA_CATEGORIES.includes(category);

export const variantQuotasForFragment = (services: QuotaBearingServicesPerTraveler | undefined): VariantUnits =>
  Object.values(services ?? {})
    .flat()
    .filter(isPresent)
    .filter(({ isService }) => isService)
    .reduce<VariantUnits>((quotas, { variant, quota }) => {
      if (!isKnownQuota(quota)) {
        return quotas;
      }

      return { ...quotas, [variant]: isPresent(quotas[variant]) ? Math.min(quotas[variant], quota) : quota };
    }, {});

export const variantUnitsAcrossTravelers = (unitsPerTraveler: VariantUnitsPerTraveler | undefined): VariantUnits =>
  Object.values(unitsPerTraveler ?? {}).reduce<VariantUnits>(
    (totals, unitsForTraveler) =>
      Object.entries(unitsForTraveler ?? {}).reduce(
        (all, [variant, units]) => ({ ...all, [variant]: (all[variant] ?? 0) + units }),
        totals
      ),
    {}
  );

export const remainingVariantQuota = (pool: number, selectedUnits: number, unpaidUnitsAtLoad: number): number =>
  Math.max(pool - (selectedUnits - unpaidUnitsAtLoad), 0);

export const remainingVariantQuotasPerFragment = (
  catalogServices: QuotaBearingServiceCatalogServices | undefined,
  selectedUnits: VariantUnitsPerFragment | undefined,
  unpaidUnitsAtLoad: VariantUnitsPerFragment | undefined
): Record<string, VariantUnits> => {
  return Object.entries(catalogServices ?? {}).reduce<Record<string, VariantUnits>>(
    (remaining, [fragmentId, servicesPerTraveler]) => {
      const pools = variantQuotasForFragment(servicesPerTraveler);

      if (isEmptyObject(pools)) {
        return remaining;
      }

      const selected = variantUnitsAcrossTravelers(selectedUnits?.[fragmentId]);
      const unpaid = variantUnitsAcrossTravelers(unpaidUnitsAtLoad?.[fragmentId]);

      return {
        ...remaining,
        [fragmentId]: mapValues(pools, (pool: number, variant: string) =>
          remainingVariantQuota(pool, selected[variant] ?? 0, unpaid[variant] ?? 0)
        ),
      };
    },
    {}
  );
};

export const unitsAcrossTravelersForVariants = (
  unitsPerTraveler: VariantUnitsPerTraveler | undefined,
  variants: string[]
): number => {
  const totals = variantUnitsAcrossTravelers(unitsPerTraveler);

  return variants.reduce((sum, variant) => sum + (totals[variant] ?? 0), 0);
};

export const remainingCategoryQuotasPerFragment = (
  catalogServices: QuotaBearingServiceCatalogServices | undefined,
  selectedUnits: VariantUnitsPerFragment | undefined,
  unpaidUnitsAtLoad: VariantUnitsPerFragment | undefined
): Record<string, number> =>
  Object.entries(catalogServices ?? {}).reduce<Record<string, number>>(
    (remaining, [fragmentId, servicesPerTraveler]) => {
      const pools = variantQuotasForFragment(servicesPerTraveler);

      if (isEmptyObject(pools)) {
        return remaining;
      }

      const quotaBearingVariants = Object.keys(pools);
      const pool = Math.min(...Object.values(pools));
      const selected = unitsAcrossTravelersForVariants(selectedUnits?.[fragmentId], quotaBearingVariants);
      const unpaid = unitsAcrossTravelersForVariants(unpaidUnitsAtLoad?.[fragmentId], quotaBearingVariants);

      return { ...remaining, [fragmentId]: remainingVariantQuota(pool, selected, unpaid) };
    },
    {}
  );

export const lowestQuotaAcrossServiceVariants = (
  services: QuotaBearingServicesPerTraveler | undefined
): number | undefined => {
  const quotas = Object.values(services ?? {})
    .flat()
    .filter(Boolean)
    .filter(({ isService }) => isService)
    .map(({ quota }) => quota)
    .filter(isPresent);

  return quotas.length ? Math.min(...quotas) : undefined;
};

export const lowestQuotaAcrossFlights = (
  services: QuotaBearingServiceCatalogServices | undefined,
  initeraryFlightIdsInTravelOrder: string[]
): FragmentQuota | undefined =>
  initeraryFlightIdsInTravelOrder.reduce<FragmentQuota | undefined>((lowestFragment, flightId) => {
    const quota = lowestQuotaAcrossServiceVariants(services?.[flightId]);

    if (!isPresent(quota)) {
      return lowestFragment;
    }

    const currentFragmentQuota: FragmentQuota = { fragmentId: flightId, quota };

    if (!lowestFragment) {
      return currentFragmentQuota;
    }

    return currentFragmentQuota.quota < lowestFragment.quota ? currentFragmentQuota : lowestFragment;
  }, undefined);

export interface LowQuotaSegment {
  count: number;
  segment: string;
}

interface LowestLowQuotaSegmentArguments {
  services: QuotaBearingServiceCatalogServices | undefined;
  bounds: FinnairBoundItem[] | undefined;
  purchasesDonePerFragment?: Record<string, boolean>;
  unpaidUnitsPerFragment?: VariantUnitsPerFragment;
  nowMillis?: number;
}

export const lowestLowQuotaSegment = ({
  services,
  bounds,
  purchasesDonePerFragment = {},
  unpaidUnitsPerFragment,
  nowMillis = Date.now(),
}: LowestLowQuotaSegmentArguments): LowQuotaSegment | undefined => {
  const quotaPerFragment = remainingCategoryQuotasPerFragment(services, undefined, unpaidUnitsPerFragment);

  const lowestQuotaFlight = (bounds ?? [])
    .flatMap((bound) => bound.itinerary.filter(isFlight))
    .filter((flight) => Date.parse(flight.departure.dateTime) > nowMillis)
    .filter((flight) => !purchasesDonePerFragment[flight.id])
    .filter((flight) => isLowQuota(quotaPerFragment[flight.id]))
    .sort((aFlight, bFlight) => Date.parse(aFlight.departure.dateTime) - Date.parse(bFlight.departure.dateTime))
    .reduce<FinnairItineraryItemFlight | undefined>((currentLowest, flight) => {
      if (!currentLowest || quotaPerFragment[flight.id] < quotaPerFragment[currentLowest.id]) {
        return flight;
      }

      return currentLowest;
    }, undefined);

  if (!lowestQuotaFlight) {
    return undefined;
  }

  const { departure, arrival } = lowestQuotaFlight;

  return {
    count: quotaPerFragment[lowestQuotaFlight.id],
    segment: `${departure.locationCode}-${arrival.locationCode}`,
  };
};

export const getCategoryLevelQuotaCount = (
  serviceCatalog: FinnairServiceCatalogV3 | undefined,
  category: Category,
  bound: FinnairBoundItem,
  fragmentIdsToConsider?: string[]
): number | undefined => {
  if (!hasCategoryLevelQuota(category) || !serviceCatalog) {
    return undefined;
  }

  const catalogForCategory = serviceCatalog.categories.find((item) => item.category === category);
  if (!catalogForCategory) {
    return 0;
  }

  const itineraryFlightId = bound.itinerary.filter(isFlight).map((flight) => flight.id);
  const sellableFlightIds = itineraryFlightId.filter((id) => Boolean(catalogForCategory.services?.[id]));
  if (sellableFlightIds.length === 0) {
    return 0;
  }

  const flightIdsToCount = fragmentIdsToConsider
    ? sellableFlightIds.filter((id) => fragmentIdsToConsider.includes(id))
    : sellableFlightIds;

  if (flightIdsToCount.length === 0) {
    return undefined;
  }

  const lowestQuota = lowestQuotaAcrossFlights(catalogForCategory.services, flightIdsToCount)?.quota;

  return lowestQuota ?? SUFFICIENT_QUOTA_SENTINEL;
};

export enum CategoryQuotaStateKind {
  NONE = 'NONE',
  ENOUGH_FOR_ALL = 'ENOUGH_FOR_ALL',
  ALL_SELECTED = 'ALL_SELECTED',
  SHORT_FOR_GROUP = 'SHORT_FOR_GROUP',
  SHORT_FOR_GROUP_NONE_LEFT = 'SHORT_FOR_GROUP_NONE_LEFT',
}

export type CategoryQuotaState =
  | { kind: CategoryQuotaStateKind.NONE }
  | { kind: CategoryQuotaStateKind.ENOUGH_FOR_ALL; remaining: number }
  | { kind: CategoryQuotaStateKind.ALL_SELECTED }
  | { kind: CategoryQuotaStateKind.SHORT_FOR_GROUP; remaining: number; passengerCount: number }
  | { kind: CategoryQuotaStateKind.SHORT_FOR_GROUP_NONE_LEFT };

export interface QuotaNotification {
  titleKey?: string;
  descriptionKey: string;
  params: Record<string, number>;
  theme: NotificationTheme;
}

interface QuotaStateCopy {
  title: string;
  description: string;
}

export interface CategoryQuotaCopy {
  enoughForAll: QuotaStateCopy;
  allSelected: QuotaStateCopy;
  shortForGroup: QuotaStateCopy;
  shortForGroupNoneLeft: { description: string };
  takenByOtherPassenger: string;
}

const pluralizedKey = (key: string, count: number): string => `${key}.${count === 1 ? 'singular' : 'plural'}`;

const categoryQuotaCopy = (root: string): CategoryQuotaCopy => ({
  enoughForAll: { title: `${root}.enoughForAll.title`, description: `${root}.enoughForAll.description` },
  allSelected: { title: `${root}.allSelected.title`, description: `${root}.allSelected.description` },
  shortForGroup: { title: `${root}.shortForGroup.title`, description: `${root}.shortForGroup.description` },
  shortForGroupNoneLeft: { description: `${root}.shortForGroup.noneLeft` },
  takenByOtherPassenger: `${root}.takenByOtherPassenger`,
});

export const CATEGORY_QUOTA_COPY: Partial<Record<Category, CategoryQuotaCopy>> = {
  [Category.MEAL]: categoryQuotaCopy('ancillaries.meals.quota'),
  [Category.PRIORITY]: categoryQuotaCopy('ancillaries.priority.quota'),
};

export const VARIANT_QUOTA_COPY_FALLBACK = 'default';

export type VariantQuotaCopyState = 'exhausted' | 'takenByOtherPassenger';

const VARIANT_QUOTA_COPY_ROOT: Partial<Record<Category, string>> = {
  [Category.SPORT]: 'ancillaries.sport.quota',
};

export const variantQuotaCopyKeys = (category: Category, variant: string, state: VariantQuotaCopyState): string[] => {
  const root = VARIANT_QUOTA_COPY_ROOT[category];

  if (!root) {
    return [];
  }

  return [`${root}.${state}.${variant}`, `${root}.${state}.${VARIANT_QUOTA_COPY_FALLBACK}`];
};

export const getCategoryQuotaState = (
  remaining: number | undefined,
  travelersWithoutSelection: number,
  passengerCount: number
): CategoryQuotaState => {
  if (!isPresent(remaining)) {
    return { kind: CategoryQuotaStateKind.NONE };
  }

  const shortForGroup = remaining < travelersWithoutSelection;

  if (remaining === 0) {
    return shortForGroup
      ? { kind: CategoryQuotaStateKind.SHORT_FOR_GROUP_NONE_LEFT }
      : { kind: CategoryQuotaStateKind.ALL_SELECTED };
  }

  if (shortForGroup) {
    return { kind: CategoryQuotaStateKind.SHORT_FOR_GROUP, remaining, passengerCount };
  }

  return isLowQuota(remaining)
    ? { kind: CategoryQuotaStateKind.ENOUGH_FOR_ALL, remaining }
    : { kind: CategoryQuotaStateKind.NONE };
};

export const getCategoryQuotaNotification = (
  state: CategoryQuotaState,
  category: Category
): QuotaNotification | undefined => {
  const copy = CATEGORY_QUOTA_COPY[category];

  if (!copy) {
    return undefined;
  }

  switch (state.kind) {
    case CategoryQuotaStateKind.ENOUGH_FOR_ALL:
      return {
        titleKey: pluralizedKey(copy.enoughForAll.title, state.remaining),
        descriptionKey: copy.enoughForAll.description,
        params: { amount: state.remaining },
        theme: NotificationTheme.INFO,
      };
    case CategoryQuotaStateKind.ALL_SELECTED:
      return {
        titleKey: copy.allSelected.title,
        descriptionKey: copy.allSelected.description,
        params: {},
        theme: NotificationTheme.INFO,
      };
    case CategoryQuotaStateKind.SHORT_FOR_GROUP:
      return {
        titleKey: pluralizedKey(copy.shortForGroup.title, state.remaining),
        descriptionKey: copy.shortForGroup.description,
        params: { amount: state.remaining, passengerCount: state.passengerCount },
        theme: NotificationTheme.WARNING,
      };
    case CategoryQuotaStateKind.SHORT_FOR_GROUP_NONE_LEFT:
      return {
        descriptionKey: copy.shortForGroupNoneLeft.description,
        params: {},
        theme: NotificationTheme.WARNING,
      };
    default:
      return undefined;
  }
};

export type VariantSelectionValues = Record<string, string | boolean | number>;

export const countOwnUnitsOfVariants = (selection: VariantSelectionValues, variants: string[]): number => {
  if (typeof selection?.['radio'] === 'string') {
    return variants.includes(selection['radio']) ? 1 : 0;
  }

  return variants.reduce((units, variant) => {
    const value = selection?.[variant];

    if (typeof value === 'number') {
      return units + value;
    }

    const selections = toBoolean(value) ? 1 : 0;
    return units + selections;
  }, 0);
};

export const quotaBearingVariantsOf = (services: QuotaBearingService[]): string[] =>
  services.filter((service) => service.isService && isKnownQuota(service.quota)).map(({ variant }) => variant);

export const paidUnitsOfVariants = (
  purchasedServices: Array<Pick<FinnairPassengerServiceSelectionItem, 'variant' | 'quantity'>>,
  variants: string[]
): number =>
  purchasedServices
    .filter(({ variant }) => variants.includes(variant))
    .reduce((units, { quantity }) => units + (quantity ?? 1), 0);

const getPassengerCountQuotaType = ({
  isSelected,
  availableQuota,
  passengersWithSelection,
  travelerHasSelection,
}: {
  isSelected: boolean;
  availableQuota: number;
  passengersWithSelection: number;
  travelerHasSelection: boolean;
}): PassengerCountQuotaType => {
  if (isSelected) {
    return PassengerCountQuotaType.SELECTED;
  }

  if (availableQuota === 0) {
    return PassengerCountQuotaType.NO;
  }

  const isQuotaToppedByCustomer =
    passengersWithSelection > 0 && passengersWithSelection >= availableQuota && !travelerHasSelection;

  if (isQuotaToppedByCustomer) {
    return PassengerCountQuotaType.TOPPED;
  }

  return PassengerCountQuotaType.FREE;
};

export const calculatePassengerCountQuota = (
  passengersWithSelection: number,
  service: { quota?: number | null },
  isSelected: boolean,
  passengersWithSelectionAtInit: number,
  travelerHasSelection: boolean
): PassengerCountQuota => {
  const quota = service.quota === null ? undefined : service.quota;
  const availableQuota = quota + passengersWithSelectionAtInit;

  return {
    type: getPassengerCountQuotaType({ isSelected, availableQuota, passengersWithSelection, travelerHasSelection }),
    count: quota !== undefined ? availableQuota - passengersWithSelection : undefined,
  };
};
